import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Plus, Pencil, Trash2, KeyRound, Copy, ShieldCheck, X, IdCard } from "lucide-react";
import { toast } from "sonner";
import { Modal, Field, Grid2, Input, Select, Th } from "@/pages/Contacts";
import ConfirmDialog from "@/components/ConfirmDialog";
import { formatPhoneDisplay } from "@/lib/format";

const ROLES = ["admin", "manager", "sales"];
const ROLE_LABELS = { admin: "Admin", manager: "Manager", sales: "Sales / Estimator" };

const empty = { name: "", email: "", role: "sales", phone: "", title: "" };

export default function Users() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const [credential, setCredential] = useState(null);
  const [confirmTarget, setConfirmTarget] = useState(null);
  const [rates, setRates] = useState({}); // user_id -> {rate_pct, payroll_type}
  const [vendors, setVendors] = useState([]);

  const load = () => {
    api.get("/users").then((r) => setItems(r.data));
    api.get("/commissions/rates")
      .then((r) => setRates(Object.fromEntries((r.data || []).map((x) => [x.user_id, x]))))
      .catch(() => {}); // non-admins: silently skip
    api.get("/vendors").then((r) => setVendors(r.data || [])).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (u) => {
    const rt = rates[u.id] || {};
    setEditing(u);
    setForm({
      name: u.name || "", email: u.email, role: u.role, phone: u.phone || "", title: u.title || "",
      commission_rate: rt.rate_pct != null ? String(rt.rate_pct) : "",
      payroll_type: rt.payroll_type || "1099",
      payee_vendor_id: u.payee_vendor_id || "",
    });
    setOpen(true);
  };

  const maybeSaveRate = async (userId) => {
    const rt = rates[userId] || {};
    const raw = (form.commission_rate ?? "").toString().trim();
    if (raw === "") return; // untouched / not on commission
    const pct = parseFloat(raw);
    if (Number.isNaN(pct) || pct < 0 || pct > 100) { toast.error("Commission rate must be 0–100"); return; }
    const unchanged = rt.rate_pct != null && Number(rt.rate_pct) === pct && (rt.payroll_type || "1099") === form.payroll_type;
    if (unchanged) return;
    await api.put(`/commissions/rates/${userId}`, { rate_pct: pct, payroll_type: form.payroll_type });
    toast.success(`Commission rate set to ${pct}%`);
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (editing) {
        const patch = { name: form.name, email: form.email, role: form.role, phone: form.phone, title: form.title };
        await api.put(`/users/${editing.id}`, patch);
        await maybeSaveRate(editing.id);
        toast.success("User updated");
        setOpen(false);
      } else {
        const r = await api.post(`/users`, form);
        toast.success("User created");
        setOpen(false);
        setCredential({ user: r.data.user, password: r.data.generated_password });
      }
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  const regen = async (u) => {
    try {
      const r = await api.post(`/users/${u.id}/regenerate-password`);
      setCredential({ user: u, password: r.data.generated_password });
      toast.success("New password generated");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || e.message);
    }
  };

  const removeConfirmed = async () => {
    if (!confirmTarget) return;
    try {
      await api.delete(`/users/${confirmTarget.id}`);
      toast.success("User deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || e.message);
    } finally {
      setConfirmTarget(null);
    }
  };

  const copy = (text) => { navigator.clipboard.writeText(text); toast.success("Copied"); };

  return (
    <div className="p-6 sm:p-8 animate-in fade-in duration-500" data-testid="users-page">
      <div className="flex items-end justify-between mb-8 pb-6 border-b border-zinc-200 gap-4 flex-wrap">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-blue-700 mb-2">Team</div>
          <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tight">Users &amp; Roles</h1>
        </div>
        <button data-testid="new-user-button" onClick={openCreate} className="inline-flex items-center gap-2 bg-blue-700 text-white px-4 h-10 text-xs font-bold uppercase tracking-wider hover:bg-blue-800 rounded-sm transition-colors">
          <Plus className="w-4 h-4" /> New User
        </button>
      </div>

      <div className="bg-white border border-zinc-200 rounded-sm overflow-x-auto">
        <table className="w-full text-sm" data-testid="users-table">
          <thead>
            <tr className="border-b-2 border-zinc-950 text-left">
              <Th>Name</Th><Th>Email</Th><Th>Role</Th><Th>Job Title</Th><Th>Phone</Th><Th>Commission</Th><Th>Actions</Th>
            </tr>
          </thead>
          <tbody>
            {items.map((u) => (
              <tr key={u.id} className="border-b border-zinc-100 hover:bg-zinc-50" data-testid={`user-row-${u.id}`}>
                <td className="px-6 py-3 font-bold text-zinc-950">
                  <Link to={`/users/${u.id}`} className="hover:text-blue-700 hover:underline" data-testid={`user-name-${u.id}`}>{u.name}</Link>
                </td>
                <td className="px-6 py-3 text-zinc-700">{u.email}</td>
                <td className="px-6 py-3">
                  <span className={`inline-block px-2 py-1 text-[10px] font-bold uppercase tracking-wider rounded-sm ${u.role === "admin" ? "bg-zinc-950 text-white" : u.role === "manager" ? "bg-blue-100 text-blue-800" : "bg-zinc-200 text-zinc-800"}`}>
                    {ROLE_LABELS[u.role] || u.role}
                  </span>
                </td>
                <td className="px-6 py-3 text-zinc-600 text-xs">{u.title || "—"}</td>
                <td className="px-6 py-3 text-zinc-600 font-mono text-xs">{formatPhoneDisplay(u.phone) || "—"}</td>
                <td className="px-6 py-3" data-testid={`user-commission-${u.id}`}>
                  {rates[u.id]?.rate_pct != null ? (
                    <span className="font-mono text-xs font-bold text-emerald-700">{Number(rates[u.id].rate_pct).toFixed(2)}% · {rates[u.id].payroll_type || "1099"}</span>
                  ) : (
                    <span className="text-xs text-zinc-400 italic">—</span>
                  )}
                  {u.payee_vendor_id && (
                    <div className="text-[10px] text-zinc-500 mt-0.5">
                      via {(vendors.find((v) => v.id === u.payee_vendor_id) || {}).name || "company"}
                    </div>
                  )}
                </td>
                <td className="px-6 py-3">
                  <div className="flex items-center gap-1">
                    <Link to={`/users/${u.id}`} title="Open profile (notes, certifications, equipment)" className="p-1.5 hover:bg-blue-100 text-blue-700 rounded-sm" data-testid={`profile-${u.id}`}><IdCard className="w-3.5 h-3.5" /></Link>
                    <button data-testid={`regen-${u.id}`} onClick={() => regen(u)} title="Regenerate password" className="p-1.5 hover:bg-zinc-200 rounded-sm"><KeyRound className="w-3.5 h-3.5" /></button>
                    <button data-testid={`edit-user-${u.id}`} onClick={() => openEdit(u)} title="Edit" className="p-1.5 hover:bg-zinc-200 rounded-sm"><Pencil className="w-3.5 h-3.5" /></button>
                    <button data-testid={`delete-user-${u.id}`} onClick={() => setConfirmTarget(u)} title="Delete" className="p-1.5 hover:bg-red-100 text-red-700 rounded-sm"><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {open && (
        <Modal title={editing ? "Edit User" : "New User"} onClose={() => setOpen(false)}>
          <form onSubmit={submit} className="space-y-4" data-testid="user-form">
            <Grid2>
              <Field label="Name *">
                <Input data-testid="user-name" required value={form.name} onChange={(v) => setForm({ ...form, name: v })} />
              </Field>
              <Field label="Email *" hint={editing ? "Changing this changes their login email." : undefined}>
                <Input data-testid="user-email" type="email" required value={form.email} onChange={(v) => setForm({ ...form, email: v })} />
              </Field>
              <Field label="Role">
                <Select data-testid="user-role" value={form.role} onChange={(v) => setForm({ ...form, role: v })} options={ROLES.map((r) => ({ value: r, label: ROLE_LABELS[r] }))} />
              </Field>
              <Field label="Job Title" hint="e.g., General Manager, Lead Estimator. Appears on Purchase Orders.">
                <Input data-testid="user-title" value={form.title} onChange={(v) => setForm({ ...form, title: v })} placeholder="e.g., General Manager" />
              </Field>
              <Field label="Phone">
                <Input data-testid="user-phone" format="phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} />
              </Field>
              {editing && (
                <>
                  <Field label="Commission Rate (% of net profit)" hint="Blank = not on commission. Changes append to the audit-safe rate history.">
                    <Input data-testid="user-commission-rate" type="number" value={form.commission_rate || ""} onChange={(v) => setForm({ ...form, commission_rate: v })} placeholder="e.g. 5" />
                  </Field>
                  <Field label="Payroll Type">
                    <Select data-testid="user-payroll-type" value={form.payroll_type || "1099"} onChange={(v) => setForm({ ...form, payroll_type: v })} options={[{ value: "1099", label: "1099 — via Payables" }, { value: "W2", label: "W2 — payroll CSV" }]} />
                  </Field>
                  <Field
                    label="Paid via Company (Vendor)"
                    hint="For reps paid through their own company: add the company under Vendors (with its Tax ID / W-9), then pick it here. Commission bills will be made out to that company."
                  >
                    <Select
                      data-testid="user-payee-vendor"
                      value={form.payee_vendor_id || ""}
                      onChange={(v) => setForm({ ...form, payee_vendor_id: v })}
                      options={[
                        { value: "", label: "— Personal (paid as individual) —" },
                        ...vendors
                          .filter((v) => !v.is_deleted)
                          .map((v) => ({ value: v.id, label: `${v.name || v.vendor_name}${v.tin_ein ? " · Tax ID on file" : " · ⚠ no Tax ID"}` })),
                      ]}
                    />
                  </Field>
                </>
              )}
            </Grid2>
            {!editing && (
              <div className="bg-blue-50 border border-blue-200 rounded-sm p-3 text-xs text-blue-900">
                A secure 12-character password will be generated and shown to you once after saving. Make sure to copy it before closing.
              </div>
            )}
            <div className="flex justify-end gap-2 pt-4 border-t border-zinc-200">
              <button type="button" onClick={() => setOpen(false)} className="px-4 h-10 text-xs font-bold uppercase tracking-wider border border-zinc-300 rounded-sm hover:bg-zinc-50">Cancel</button>
              <button type="submit" disabled={loading} data-testid="user-save" className="px-4 h-10 text-xs font-bold uppercase tracking-wider bg-blue-700 text-white hover:bg-blue-800 rounded-sm disabled:opacity-50">{loading ? "Saving..." : (editing ? "Save" : "Create User")}</button>
            </div>
          </form>
        </Modal>
      )}

      {credential && (
        <div className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-4" data-testid="credential-modal">
          <div className="bg-white rounded-sm shadow-2xl w-full max-w-md">
            <div className="px-6 py-4 border-b border-zinc-200 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <ShieldCheck className="w-5 h-5 text-blue-700" />
                <h2 className="font-heading text-lg font-bold tracking-tight">Password Generated</h2>
              </div>
              <button onClick={() => setCredential(null)} className="p-1.5 hover:bg-zinc-100 rounded-sm"><X className="w-4 h-4" /></button>
            </div>
            <div className="p-6 space-y-4">
              <div className="text-sm text-zinc-700">
                Share these credentials securely with <b>{credential.user.name}</b>. <b>This password will not be shown again.</b>
              </div>
              <div className="bg-zinc-50 border border-zinc-200 rounded-sm p-4 space-y-3">
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Email</div>
                  <div className="flex items-center justify-between gap-2">
                    <div className="font-mono text-sm break-all">{credential.user.email}</div>
                    <button onClick={() => copy(credential.user.email)} className="p-1 hover:bg-zinc-200 rounded-sm"><Copy className="w-3.5 h-3.5" /></button>
                  </div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Temporary Password</div>
                  <div className="flex items-center justify-between gap-2">
                    <div className="font-mono text-sm font-bold text-blue-700" data-testid="generated-password">{credential.password}</div>
                    <button onClick={() => copy(credential.password)} className="p-1 hover:bg-zinc-200 rounded-sm" data-testid="copy-password"><Copy className="w-3.5 h-3.5" /></button>
                  </div>
                </div>
              </div>
              <button onClick={() => setCredential(null)} data-testid="credential-close" className="w-full h-10 bg-blue-700 text-white text-xs font-bold uppercase tracking-wider rounded-sm hover:bg-blue-800">
                I've Saved It — Close
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={!!confirmTarget}
        title="Delete User?"
        message={`Permanently delete ${confirmTarget?.name}? They will immediately lose access. This cannot be undone.`}
        onConfirm={removeConfirmed}
        onClose={() => setConfirmTarget(null)}
      />
    </div>
  );
}

